import { MultiColumnChartDataType } from "@/components/graphs/WinRate/MultiColumnBarChart";
import { UserData } from "@/entities/users/types";

export enum ReportingAction {
  PIPELINE_VALUE_OVER_TIME = "pipeline_value_over_time"
}

export enum ReportingBreakdown {
  NONE = "none"
}

export enum ReportType {
  KPIS = "kpis",
  WIN_RATE = "win_rate",
  MAPPING_SCORE = "mapping_score",
  PIPELINE_FUNNEL = "sales_pipeline_funnel",
  CONVERSION_RATES = "conversion_rates",
  AVG_TIME_SPENT = "avg_time_spent",
  CHAMPION_MAPPING_SCORE = "champions_mapping_score",
  KEY_STAKEHOLDERS_MAPPING_SCORE = "key_stakeholders_mapping_score",
  QUOTA_ACHIEVEMENT = "quota_achievement",
  LEAD_ANALYSIS = "leads_analysis",
  SALES_VS_QUOTA = "sales_vs_quota",
  RETENTION = "retention",
  PIPELINE_OVER_TIME = "pipeline_over_time"
}

export interface ReportResponse {
  [key: string]: ReportData;
}

export interface ReportMetadata {
  currency?: boolean;
  period?: number;
}

export interface ReportBaseData {
  type: string;
  value: number;
  metadata?: ReportMetadata;
}

export interface SingleValueData extends ReportBaseData {
  value: number;
  metadata?: ReportMetadata;
}

export interface ComparisonData extends ReportBaseData {
  value: number;
  comparedTo: number;
  metadata?: ReportMetadata;
}

export interface PeerComparisonData extends ReportBaseData {
  you: ContributorDualValue;
  team: ContributorDualValue;
  org: ContributorDualValue;
  industry: ContributorDualValue | null;
  metadata?: ReportMetadata;
}

export type ReportData = Record<
  string,
  SingleValueData | ComparisonData | PeerComparisonData
>;

// Sales and Pipeline interfaces
export enum SettingsPeriod {
  WEEK = 1,
  MONTH = 2,
  QUARTER = 3,
  YEAR = 4,
  ROLLING_YEAR = 5,
  ALL_TIMES = 11,
  YTD = 12,
  LAST_12_MONTHS = 13,
  LAST_24_MONTHS = 14
}

export type SettingsPeriodString =
  | "week"
  | "month"
  | "quarter"
  | "year"
  | "rollingYear"
  | "allTimes"
  | "ytd"
  | "last12m"
  | "last24m";

export interface ContributorDualValue {
  value: number;
  weightedValue: number;
}

export interface ContributorValues extends ContributorDualValue {
  avgProbability?: number;
  user?: UserData;
}

export interface StageData {
  stageId: number;
  stageOrder: number;
  stageName?: string;
  contributors: Record<string, ContributorValues>;
  totalValue?: number;
  totalWeightedValue?: number;
}

export interface PipelineFunnelData {
  stages: StageData[];
  period: SettingsPeriod;
  isArr: boolean;
}

// Can refer to either stage or milestone
export interface ConversionPhaseData {
  id: number;
  conversions: {
    allToCurrent: number;
    currentToWin: number;
    team?: number;
    org?: number;
    industry?: number;
  };
}

export interface ConversionRatesFunnelData {
  metadata?: ReportMetadata;
  stages: ConversionPhaseData[];
  milestones: ConversionPhaseData[];
  timeFrame: SettingsPeriod;
}

interface DirectReportConversionRates {
  id: string;
  stages: {
    id: number;
    conversions: {
      allToCurrent: number;
      currentToWin: number;
    };
  }[];
  milestones: {
    id: number;
    conversions: {
      allToCurrent: number;
      currentToWin: number;
    };
  }[];
}

export interface ConversionRatesDirectReportsResponse {
  directReports: DirectReportConversionRates[];
  timeFrame: number;
}

export interface ChartDataType {
  date: string;
  youValue: number;
  teamValue: number;
  organizationValue: number;
  industryValue?: number;
}

export interface ChartSingleValue {
  you: number;
  team: number;
  org: number;
  industry?: number;
}
export interface WinRateOverallData {
  winRates: ChartSingleValue;
  timeFrame: SettingsPeriod;
}

export interface WinRateOverallDataDirectReports {
  winRates: {
    [key: string]: number;
  };
  timeFrame: SettingsPeriod;
}

export type WinRateOverTimeFrame = "toMonth" | "byMonth";

export interface WinRateOverTimeDate {
  toMonth: { [key: string]: ChartSingleValue };
  byMonth: { [key: string]: ChartSingleValue };
}

export interface WinRateOverTimeDateDirectReports {
  toMonth: {
    [key: string]: {
      [key: string]: number;
    };
  };
  byMonth: {
    [key: string]: {
      [key: string]: number;
    };
  };
}

export interface WinRateData {
  winRate: {
    overall: WinRateOverallData;
    overTime: WinRateOverTimeDate;
    period: SettingsPeriod;
  };
}

export interface WinRateDirectReportsResponse {
  overall: WinRateOverallDataDirectReports;
  overTime: WinRateOverTimeDateDirectReports;
}

export interface AverageTimeSpentPhaseData {
  id: number;
  stageOrder?: number;
  averageTimeSpentValues: {
    you: number;
    team: number;
    org: number;
    industry?: number;
  };
}

export interface AverageTimeSpentData {
  metadata?: ReportMetadata;
  stages: AverageTimeSpentPhaseData[];
  milestones: AverageTimeSpentPhaseData[];
  timeFrame: SettingsPeriod;
}

interface DirectReportsAverageTimeSpent {
  id: string;
  stages: AverageTimeSpentPhaseData[];
  milestones: AverageTimeSpentPhaseData[];
}

export interface AverageTimeSpentDirectReportsResponse {
  directReports: DirectReportsAverageTimeSpent[];
  timeFrame: number;
}

export interface OverallScoringData {
  averageScore: number;
  trend: {
    scoreForPeriod: number;
    comparedToScore: number;
  };
  probability?: number;
}

export interface DirectReportScoringData {
  id: string;
  directReportsNum: number;
  weakDealsNum: number;
  avgScore: number;
}

export interface DirectReportQuotaData {
  id: string;
  directReportsNum: number;
  arrMrrForPeriod: number;
  quotaAchievement: number;
}

export type ScoreType =
  | "keyStakeholdersMappingScore"
  | "championsMappingScore"
  | "quotaAchievement"
  | "leadAnalysis";

export type ScoringForDirectReportsResponse = {
  [k in ScoreType]?: {
    data: {
      directReports: (
        | DirectReportScoringData
        | DirectReportQuotaData
        | LeadAnalysisDirectReport
      )[];
    };
    period: SettingsPeriod;
  };
};

export type OverallScoringResponse = {
  [k in ScoreType]?: {
    overall: OverallScoringData;
    period: SettingsPeriod;
    isArr?: boolean;
  };
};

export interface MonthlyMetric {
  you: number;
  team: number;
  org: number;
}

export interface TrendType {
  valueForPeriod: number;
  comparedToValue: number;
}

export interface OverallMetric {
  value: number;
  trend: TrendType;
  team: number;
  org: number;
  industry?: number;
}

export interface LeadSourceMonthlyMetric {
  source: string | null;
  byMonths: {
    [key: string]: number;
  };
}

export interface MetricData {
  overall: OverallMetric;
  byMonths: {
    [key: string]: MonthlyMetric;
  };
  bySource?: LeadSourceMonthlyMetric[] | null;
}

export interface LeadAnalysis {
  avgMonthlyQualifiedConvertedLeads: MetricData;
  leadQualificationRate: MetricData;
  leadConversionRate: MetricData;
  timeFrame: SettingsPeriod;
}

export interface LeadAnalysisData {
  leadsAnalysis: LeadAnalysis;
}

type DirectReportMetric = {
  value: number;
  trend: TrendType;
};

export type LeadAnalysisDirectReportOverall = {
  id: string;
  directReportsNum: number;
  avgMonthlyQualifiedConvertedLeads: DirectReportMetric;
  leadQualificationRate: DirectReportMetric;
  leadConversionRate: DirectReportMetric;
};

export type DirectReportTable = {
  id: string;
  directReportsNum: number;
  avgMonthlyQualifiedConvertedLeads: DirectReportMetric;
  leadQualificationRate: DirectReportMetric;
  leadConversionRate: DirectReportMetric;
  user?: UserData;
};

export type DirectReportMonthlyData = {
  byMonth: {
    [month: string]: {
      [directReportId: string]: number;
    };
  };
};

export type LeadAnalysisDirectReport = {
  overall: LeadAnalysisDirectReportOverall[];
  avgMonthlyQualifiedConvertedLeads: DirectReportMonthlyData;
  leadQualificationRate: DirectReportMonthlyData;
  leadConversionRate: DirectReportMonthlyData;
  timeFrame: number;
};

export type LeadAnalysisDirectReports = {
  directReports: {
    overall: LeadAnalysisDirectReportOverall[];
    avgMonthlyQualifiedConvertedLeads: DirectReportMonthlyData;
    leadQualificationRate: DirectReportMonthlyData;
    leadConversionRate: DirectReportMonthlyData;
    timeFrame: number;
  };
};

export interface LeadAnalysisDirectReportsResponse {
  leadAnalysis: LeadAnalysisDirectReports;
}

export type MonthlyQuotaWidget = MultiColumnChartDataType[];

export type QuarterlyQuotaWidget = MultiColumnChartDataType[];

export type AnnualQuotaWidget = {
  year: number;
  endMonth: number;
  endYear: number;
  startMonth: number;
  startYear: number;
  series: {
    [seriesKey: string]: { value: number };
  };
}[];

export interface SalesVsQuotaData {
  salesVsQuota: {
    overall: {
      quotaAchievement: ChartSingleValue;
      yearlySalesVsQuota: {
        you: {
          actualSales: { value: number };
          quota: { value: number };
        };
      };
    };
    monthlyQuotaWidget?: MonthlyQuotaWidget;
    quarterlyQuotaWidget?: QuarterlyQuotaWidget;
    annualQuotaWidget?: AnnualQuotaWidget;
  };
}

export interface RetentionMetrics {
  nrr: SingleValueData; // Net Revenue Retention
  grr: SingleValueData; // Gross Revenue Retention
}

/** One column of the revenue breakdown. Contraction and churn are positive magnitudes. */
export interface RevenueMovementColumn {
  opening: number;
  new: number;
  expansion: number;
  upsell: number;
  crossSell: number;
  contraction: number;
  churn: number;
  closing: number;
  netNew: number;
}

export interface RevenueMovementTable {
  arr: RevenueMovementColumn;
  carr: RevenueMovementColumn;
}

export interface CurrentRecurringRevenue {
  arr: number;
  carrFyExit: number;
  contractedChange: number;
  arrYoy?: number;
  carrYoy?: number;
}

export interface NetNewRevenue {
  arr?: number;
  carr?: number;
  arrYoy?: number;
  carrYoy?: number;
}

export interface RevenueRetention {
  nrr?: number;
  grr?: number;
  opening: number;
  expansion: number;
  upsell: number;
  crossSell: number;
  contraction: number;
  churn: number;
  closing: number;
}

export interface AcvTcvValues {
  acv: number;
  tcv: number;
}

export interface NewBookings {
  acv: number;
  tcv: number;
  acvYoy?: number;
  tcvYoy?: number;
  newBusiness: AcvTcvValues;
  expansion: AcvTcvValues;
  upsell: AcvTcvValues;
  crossSell: AcvTcvValues;
  recurring: AcvTcvValues;
  oneTime: AcvTcvValues;
}

/**
 * Fiscal-year revenue KPIs. The server omits optional fields it could not compute.
 * Recurring figures are in the account's unit: monthly when `isArr` is false,
 * under the same `arr` / `carr` field names.
 */
export interface RevenueKPIs {
  isArr?: boolean;
  currentRecurringRevenue: CurrentRecurringRevenue;
  netNewRevenue: NetNewRevenue;
  newBookings: NewBookings;
  retention?: RevenueRetention;
  breakdown?: RevenueMovementTable;
}

/** The body of the KPIs report, under the response's `kpis` key. */
export interface KPIsReportData {
  overallSalesYtd?: SingleValueData[];
  closingPower?: SingleValueData[];
  salesVsQuota?: ComparisonData[];
  committed?: SingleValueData[];
  /** The classic bar's retention card. The revenue bar reads `revenue.retention`. */
  retention?: SingleValueData[];
  revenue?: RevenueKPIs;
}
